import { applyHomography, invert3, multiply3, quadHomography } from '~/lib/homography'
import { normalizeRef } from '~/lib/outline'
import { applyTransform } from '~/lib/outline/object'
import { orientedAnchors, outlineGeometry, polygonArea, segmentAngle } from '~/lib/outline/path'
import { isConvexQuad } from '~/lib/surfaceGeometry'

const EPS = 1e-9

// Segment `edge` needs a chord for its direction; the frame's extent comes from the drawn outline, so a 2-anchor curve (a lens) frames.
const MIN_ANCHORS = 2

// Coordinates in axes turned `deg` clockwise on screen (y down).
const rotation = deg => {
	const r = (deg * Math.PI) / 180
	const c = Math.cos(r)
	const s = Math.sin(r)
	return [c, s, 0, -s, c, 0, 0, 0, 1]
}

const scale = (sx, sy) => [sx, 0, 0, 0, sy, 0, 0, 0, 1]

const IDENTITY = scale(1, 1)

const length = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1])

// A quad's map onto a rectangle its mean side lengths, so a band keeps its size on every surface and a trapezoid's aspect is neither of its parallel sides.
const quadMetric = q => {
	const inv = invert3(quadHomography(q))
	const w = (length(q[0], q[1]) + length(q[3], q[2])) / 2
	const h = (length(q[0], q[3]) + length(q[1], q[2])) / 2
	return inv && multiply3(scale(w, h), inv)
}

// The outline as drawn, in stage space: curves flattened (`outlineGeometry`, cached by `d`), placed by its `transform` (absent = identity).
const drawnPolygon = outline => {
	const polygon = outlineGeometry(outline?.d)?.polygon
	if (!polygon?.length) return null
	return outline.transform ? polygon.map(p => applyTransform(outline.transform, p)) : polygon
}

// Isometric axes along segment `edge`'s chord, so an offset means the same angle in the frame as on screen.
const edgeMap = (anchors, edge) => rotation(segmentAngle(anchors, edge))

// The map into metric frame axes; perspective only over a convex quad of anchors.
const baseMap = (ref, anchors, stage) => {
	if (ref.mode === 'stage') return quadMetric(stage)
	if (ref.mode === 'screen') return IDENTITY
	const oriented = orientedAnchors(anchors, ref.edge)
	// ⚠️ Convex only: a dent or an anchor on a segment puts the vanishing line inside the surface.
	// ⚠️ A quad wound the other way (drawn counter-clockwise, or flipped) maps onto the square mirrored; walked backwards from anchor 3, its 0→1 segment still runs along x and the picture faces the way 'edge' shows it.
	if (ref.mode === 'perspective' && isConvexQuad(oriented))
		return quadMetric(polygonArea(oriented) < 0 ? [...oriented].reverse() : oriented)
	return edgeMap(anchors, ref.edge)
}

// `pts`' box under `m`, or null when one of them sits on or across the map's vanishing line (a curve bulging that far past a perspective quad).
const span = (m, pts) => {
	let x0 = Infinity
	let y0 = Infinity
	let x1 = -Infinity
	let y1 = -Infinity
	let sign = 0
	for (const p of pts) {
		const w = m[6] * p[0] + m[7] * p[1] + m[8]
		if (!(Math.abs(w) > EPS) || (sign && Math.sign(w) !== sign)) return null
		sign = Math.sign(w)
		const [x, y] = applyHomography(m, p)
		x0 = Math.min(x0, x)
		x1 = Math.max(x1, x)
		y0 = Math.min(y0, y)
		y1 = Math.max(y1, y)
	}
	return [x0, y0, x1, y1]
}

/**
 * World → 0–1 frame as one 3×3 (divide by the third component), plus its extent. `outline` is a `resolveOutline` result: the 0–1 square spans its drawn (flattened) outline's bounds in the frame's own axes, so a curve bulging past its anchors stays inside; segment `ref.edge`'s chord over `outline.anchors` (stage space) is the u axis. `stage` is the node's placed quad, TL TR BR BL, and the whole of a `mode: 'stage'` frame (the outline is not read).
 * A curve bulging across a perspective quad's vanishing line falls back to the edge frame.
 */
export const referenceFrame = (ref, outline, stage) => {
	const r = normalizeRef(ref)
	const anchors = outline?.anchors
	if (r.mode === 'stage' ? stage?.length !== 4 : !(anchors?.length >= MIN_ANCHORS)) return null
	const pts = r.mode === 'stage' ? stage : drawnPolygon(outline)
	if (!pts) return null
	const offset = rotation(r.offset ?? 0)
	const base = baseMap(r, anchors, stage)
	let m = base && multiply3(offset, base)
	let box = m && span(m, pts)
	if (!box && r.mode === 'perspective') {
		m = multiply3(offset, edgeMap(anchors, r.edge))
		box = span(m, pts)
	}
	if (!box) return null

	const [x0, y0, x1, y1] = box
	const ex = x1 - x0
	const ey = y1 - y0
	if (!(ex > EPS && ey > EPS)) return null

	const matrix = multiply3([1 / ex, 0, -x0 / ex, 0, 1 / ey, -y0 / ey, 0, 0, 1], m)
	const w = matrix[8]
	return { matrix: Math.abs(w) > EPS ? matrix.map(v => v / w) : matrix, extent: [ex, ey] }
}

export const frameUV = (frame, p) => applyHomography(frame.matrix, p)

/** The frame narrowed about its centre so a picture of `aspect` (w / h) covers it unstretched. */
export const coverFrame = (frame, aspect) => {
	const af = frame.extent[0] / frame.extent[1]
	if (!(aspect > 0) || !(af > 0)) return frame
	const sx = af > aspect ? 1 : af / aspect
	const sy = af > aspect ? aspect / af : 1
	return { ...frame, matrix: multiply3([sx, 0, (1 - sx) / 2, 0, sy, (1 - sy) / 2, 0, 0, 1], frame.matrix) }
}

/** A video node's surface references, or null when it paints its own rect. */
export const nodeRefs = obj => (Array.isArray(obj?.surfaces) && obj.surfaces.length ? obj.surfaces : null)

/** The node's picture corners on the stage, TL TR BR BL: the box `x + R·sign(S)·(p − pivot)`. A mirror flips the box about the pivot, which flips the picture with it; the scale's magnitude is not applied. */
export const nodeQuad = ({ x, y, width, height, rotation = 0, scaleX = 1, scaleY = 1, pivotX, pivotY }) => {
	const r = (rotation * Math.PI) / 180
	const c = Math.cos(r)
	const s = Math.sin(r)
	const fx = scaleX < 0 ? -1 : 1
	const fy = scaleY < 0 ? -1 : 1
	const px = (pivotX ?? 0.5) * width
	const py = (pivotY ?? 0.5) * height
	return [
		[0, 0],
		[1, 0],
		[1, 1],
		[0, 1],
	].map(([u, v]) => {
		const bx = fx * (u * width - px)
		const by = fy * (v * height - py)
		return [x + c * bx - s * by, y + s * bx + c * by]
	})
}

/** The frame's 0–1 square in world coordinates, in corner order — what the editor draws. */
export const frameQuad = frame => {
	const inv = invert3(frame.matrix)
	return (
		inv &&
		[
			[0, 0],
			[1, 0],
			[1, 1],
			[0, 1],
		].map(p => applyHomography(inv, p))
	)
}

/** A `mode: 'flow'` draw's frame: the edge frame's matrix (what the editor draws; the shader reads `vUV` from the field instead) with the flow field's `extent` — river length × mean width, so `uResolution` sizes a pattern along the bends. `scale` is stage units per field unit (an object's placement, 1 for a surface). Null without a usable extent. */
export const flowFrame = (frame, field, scale = 1) => {
	const [ex, ey] = field?.extent ?? []
	if (!frame || !(ex > EPS && ey > EPS && scale > 0)) return null
	return { ...frame, extent: [ex * scale, ey * scale], flow: true }
}
